﻿import React, { Fragment, useState, useEffect } from 'react';
import { Intent } from '@blueprintjs/core'
import { FormHeading, FormHelperText, FormRadio, FormSelect, FormSwitch, FormTextArea, FormTextInput } from "components/form-fields";
import { NotificationInline, NotificationToaster, AlertUnsavedData } from "components/notifications";
import { ButtonSave } from "components/buttons";
import { ShowHide } from "components/layout";
import { useValidation } from "hooks/useValidation";
import { useDirtyData } from "hooks/useDirtyData";
import { AccountService, UserService } from "services";
import { UserAccess } from 'components/user-access';
import _ from 'underscore';
import axios from 'axios';
import { InstantReportButton } from 'components/reporting';
import { useSelector } from 'react-redux';

export function AccountGeneralSettings(props) {
	const [generalSettings, setGeneralSettings] = useState({
		companyMessage: "",
		sectorId: null,
		allowDriverAppAccess: false,
		driverAppActivationCode: "",
		driverAppMessage: "",
		restrictedAccessType: ""
	});
	const [originalValues, setOriginalValues] = useState({});
	const [sectors, setSectors] = useState([]);
	const [showSectorDanger, setSectorDanger] = useState(false);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const isDataDirty = useDirtyData(originalValues, generalSettings);
	const [isValid, errors, validate] = useValidation();

	// Restricted access
	const [restrictedAccessOptions, setRestrictedAccessOptions] = useState([]);
	const [mappedRestrictedAccessOptions, setMappedRestrictedAccessOptions] = useState([]);
	const [restrictedAccessReportGenerationId, setRestrictedAccessReportGenerationId] = useState(null);

	const userRole = useSelector(state => state.loggedInUser.role);

	useEffect(initialLoad, []);
	useEffect(validateModelIfDirty, [isDataDirty, generalSettings]);

	useEffect(() => {
		if (restrictedAccessOptions && restrictedAccessOptions.length > 0) {
			const mappedOptions = restrictedAccessOptions.map((option) => {
				return {
					label: option.name,
					value: option.key,
					helperText: option.description
				}
			});
			setMappedRestrictedAccessOptions(mappedOptions);
		}
	}, [restrictedAccessOptions]);

	function initialLoad() {
		axios.all([
			AccountService.getSectors(),
			AccountService.getGeneralSettings(),
			AccountService.getRestrictedAccessOptions()
		]).then(axios.spread(function (getSectorsResponse, generalSettingsResponse, restrictedAccessOptionsResponse) {
			setSectors(getSectorsResponse);
			setGeneralSettings(generalSettingsResponse);
			setOriginalValues(_.extend({}, generalSettingsResponse));
			setRestrictedAccessOptions(restrictedAccessOptionsResponse.restrictedAccessType);
		}), () => {
			NotificationToaster.show(Intent.DANGER, "Could not load details. Please refresh the page.");
		})
			.catch(() => {
				NotificationToaster.show(Intent.DANGER, "Could not load details. Please refresh the page.");
			})
			.finally(() => {
				setLoading(false);
			});
	}

	function saveGeneralSettings() {
		setSaving(true);
		if (!isValid) {
			NotificationToaster.show(Intent.WARNING, "Settings not valid. Please check your settings and try again.");
			setSaving(false);
			return;
		}
		AccountService.saveGeneralSettings(generalSettings).then(
			() => {
				setOriginalValues(_.extend({}, generalSettings));
				setSaving(false);
				NotificationToaster.show(Intent.SUCCESS, "Settings updated successfully.");
			},
			(error) => {
				setSaving(false);
				NotificationToaster.show(Intent.DANGER, "Unable to update settings. Please try again later.");
			});
	}

	function handleMessageChange(event) {
		setGeneralSettings({
			...generalSettings,
			companyMessage: event.target.value
		});
	}

	function handleDriverAppMessageChange(event) {
		setGeneralSettings((prev) => {
			return {
				...prev,
				driverAppMessage: event.target.value
			}
		});
	}

	function onSectorChange(item, originalValue) {
		if (originalValue !== null) {
			setGeneralSettings({
				...generalSettings,
				sectorId: item.id
			});
			setSectorDanger(item.id !== originalValues.sectorId);
		}
	}

	function onAppAccessChange(event) {
		setGeneralSettings({
			...generalSettings,
			allowDriverAppAccess: event.target.checked
		});
	}

	function onRestrictedAccessChange(event) {
		setGeneralSettings({
			...generalSettings,
			restrictedAccessType: event.target.value
		});
	}

	function validateModelIfDirty() {
		if (isDataDirty) {
			validateModel();
		}
	}

	function validateModel() {
		const rules = getRules();
		validate(rules, generalSettings);
	}

	function getRules() {
		let rules = [
			{ fieldName: "sectorId", required: true },
			{ fieldName: "restrictedAccessType", required: true }
		];
		return rules;
	}

	function generateReport() {
		UserService.generateInternalAccessReport().then((response) => {
			setRestrictedAccessReportGenerationId(response);
		}, () => {
			NotificationToaster.show(Intent.DANGER, "Unable to generate report. Please try again later.");
		}).catch(() => {
			NotificationToaster.show(Intent.DANGER, "Unable to generate report. Please try again later.");
		});
	}

	function generateRestrictedAccessReportComplete() {
		setRestrictedAccessReportGenerationId(null);
	}

	return (
		<div className="row">
			<h2>General Settings</h2>
			<FormSelect
				loading={loading}
				disabled={saving}
				items={sectors}
				onItemSelect={onSectorChange}
				placeholder="Select a sector"
				headingText="The sector of your organisation:"
				helperText={(<p>This is used to compare you against similar operators in our dashboards and self-service reporting suites.</p>)}
				dangerHelperText={errors.sectorId}
				selectedValue={generalSettings.sectorId}>
			</FormSelect>
			<NotificationInline
				loading={loading}
				show={showSectorDanger}
				text="By changing your default sector, you are altering all your historic data benchmarks against the sector average."
				intent="danger">
			</NotificationInline>
			<FormTextArea
				maxLength={255}
				showCounter
				disabled={saving}
				loading={loading}
				onChange={handleMessageChange}
				helperText={(<p>This will appear when you and your team log in to Vision.</p>)}
				headingText="Set Vision message to your company users:"
				value={generalSettings.companyMessage}>
			</FormTextArea>

			<UserAccess perform={["DriverApp:Approve"]}
				yes={() => (
					<Fragment>
						<FormSwitch
							disabled={saving}
							loading={loading}
							label="Vision Driver App access:"
							helperText={(
								<div>
									<p>Once you have turned on Vision Driver App access, a Driver activation code will be displayed. This will be required by drivers within your organisation when they register for access via the app.</p>
									<p>Once a driver has requested access via the app, an alert will be displayed within Vision allowing you to authorise or decline the request.</p>
								</div>
							)}
							checked={generalSettings.allowDriverAppAccess}
							onChange={onAppAccessChange}>
						</FormSwitch>
						<ShowHide evaluator={generalSettings.allowDriverAppAccess}
							show={(
								<Fragment>
									<FormTextArea
										maxLength={255}
										showCounter
										disabled={saving}
										loading={loading}
										onChange={handleDriverAppMessageChange}
										helperText={(<p>This will appear when your drivers log into the app.</p>)}
										headingText="Set message for the Driver App"
										value={generalSettings.driverAppMessage}>
									</FormTextArea>
									<FormTextInput
										loading={loading}
										headingText="Driver activation code:"
										disabled={true}
										value={generalSettings.driverAppActivationCode}>
									</FormTextInput>
								</Fragment>
							)}>
						</ShowHide>
					</Fragment>
				)}
			/>

			<FormRadio
				id="account-restricted-access-toggle"
				headingText="Restrict Logistics UK Access"
				helperText="Please select the level of access you would like to give Logistics UK staff to your account and it's data. To get the best level of service from our customer service and member advice teams, it is recommended to leave this set to grant all."
				loading={loading}
				options={mappedRestrictedAccessOptions}
				selectedValue={generalSettings.restrictedAccessType}
				onChange={onRestrictedAccessChange}
			></FormRadio>

			<ShowHide evaluator={userRole === "PrimaryContact" && originalValues.restrictedAccessType === "PARTIALACCESS"} show={
				<div>
					<div className='spacer-bottom'>
						<FormHeading headingLevel="h4" loading={loading}>Restricted Access Report</FormHeading>
						<FormHelperText loading={loading}>To generate a report of which Logistics UK staff have access to your account, please {" "}
							<InstantReportButton
								buttonType={"Link"}
								key={"restricted-access-report-button"}
								text={"click here "}
								onReportGenerate={generateReport}
								onReportDownloadComplete={generateRestrictedAccessReportComplete}
								reportId={restrictedAccessReportGenerationId}
							/>
						</FormHelperText>
					</div>

					<NotificationInline
						intent="info"
						text="Your report has been queued, and will download as soon as it's ready."
						show={restrictedAccessReportGenerationId !== null}
					/>
				</div>
			} />

			<AlertUnsavedData
				isDirty={isDataDirty && !loading}>
			</AlertUnsavedData>
			<ButtonSave
				loading={loading}
				onClick={saveGeneralSettings}
				disabled={saving}
			></ButtonSave>
		</div>
	);

}